import {
    type Membership,
    type MembershipProfileAnswerUpsertDto,
    type MembershipProfileAnswerWithQuestion,
    type ProfileAnswer,
    type QuestionKey,
} from '@fruitful/member-profile';

import { getPartnerDisplayFirstName } from '../../common/utils/userUtils';
import {
    captureDebug,
    captureException,
    captureMessage,
    captureWarning,
} from '../../logger/Logger';
import type { User } from '../../user/types/User';

export const MemberProfileLogger = {
    captureMessage,
    captureWarning,
    captureException,
    captureDebug,
};

/** Subtitle for financial-profile screens; null for solo memberships. */
export const getMemberProfileUserSubtitle = (
    authedUser: User,
    targetUser: {
        id?: string | null;
        firstName?: string | null;
    },
): string | null => {
    if (authedUser.partnerMember == null) {
        return null;
    }

    if (targetUser.id === authedUser.id) {
        return targetUser.firstName ?? null;
    }

    return getPartnerDisplayFirstName(targetUser.firstName);
};

export const transformUserToMemberProfileMembership = (
    user: User,
): Membership => {
    const { membership, partnerMember } = user;

    const { id: membershipId, guideId, guide, createdAt } = membership ?? {};

    if (membershipId == null || createdAt == null) {
        throw new Error(
            'Missing expected data to transform user to member profile membership object',
        );
    }
    const transformUser = ({
        id,
        type,
        email,
        firstName,
        lastName,
        pronouns,
        dateOfBirth,
        userAddress,
        taxFiling,
        employmentStatus,
        insuranceCoverage,
        syncteraKycStatus,
    }: User) => ({
        id,
        type,
        email,
        firstName,
        lastName,
        pronouns,
        dateOfBirth,
        userAddress,
        taxFiling,
        employmentStatus,
        insuranceCoverage,
        syncteraKycStatus,
    });

    const users = [transformUser(user)];

    if (partnerMember != null) {
        users.push(transformUser(partnerMember as User));
    }

    return {
        id: membershipId,
        createdAt,
        guideId,
        guide: guide ?? undefined,
        users,
    };
};

export const findMembershipProfileAnswerByQuestionKey = <
    T extends ProfileAnswer,
>(
    answers: Array<MembershipProfileAnswerWithQuestion<T>>,
    questionKey: QuestionKey,
): MembershipProfileAnswerWithQuestion<T> | undefined => {
    return answers.find(
        (answer) =>
            answer.membershipProfileQuestion.questionKey === questionKey,
    );
};

export const transformDataToMembershipProfileAnswerUpsertDto = <
    T extends ProfileAnswer,
>(
    answer: T,
    questionKey: QuestionKey,
    membership: Membership,
    user: User,
): MembershipProfileAnswerUpsertDto<T> => {
    return {
        membershipId: membership.id,
        answeredByUserId: user.id,
        answer,
        questionKey,
    };
};

export const transformMembershipProfileAnswerToUpsertDto = <
    T extends ProfileAnswer,
>(
    profileAnswer: MembershipProfileAnswerWithQuestion<T>,
): MembershipProfileAnswerUpsertDto<T> => {
    const {
        membershipId,
        answeredByUserId,
        answer,
        membershipProfileQuestion,
    } = profileAnswer;
    return {
        membershipId,
        answeredByUserId,
        answer,
        questionKey: membershipProfileQuestion.questionKey,
    };
};
